import React, { useMemo, useState, useEffect } from "react";
import { AlertTriangle, X } from "lucide-react";

/* Phase 8 — top-of-workspace trial banner.
 *
 * Only renders for plan === "free". Tier is computed from percent_used:
 *   0-49%   → null (no banner)
 *   50-79%  → yellow
 *   80-99%  → orange
 *   100%    → red + composer-disabled flag (consumed by ChatPane)
 *
 * Dismissals are per-workspace, per-tier in localStorage so an escalation
 * (yellow → orange) re-shows the banner instead of staying suppressed.
 */

const PCT_THRESHOLDS = {
  yellow: 50,
  orange: 80,
  red: 100,
};
const TIER_STYLES = {
  yellow: {
    bg: "bg-[#FAF1D1]",
    border: "border-[#D9A23A]/55",
    text: "text-[#5E4012]",
    icon: "text-[#D9A23A]",
  },
  orange: {
    bg: "bg-[#FBE3D3]",
    border: "border-[#D96C4A]/55",
    text: "text-[#5E2812]",
    icon: "text-[#D96C4A]",
  },
  red: {
    bg: "bg-[#F8D6CE]",
    border: "border-[#A8351F]/65",
    text: "text-[#5E1812]",
    icon: "text-[#A8351F]",
  },
};

function pctToTier(pct) {
  const p = Number(pct || 0);
  if (p >= PCT_THRESHOLDS.red) return "red";
  if (p >= PCT_THRESHOLDS.orange) return "orange";
  if (p >= PCT_THRESHOLDS.yellow) return "yellow";
  return null;
}

function fmtUsd(cents) {
  return `$${(Number(cents || 0) / 100).toFixed(2)}`;
}

/** Pure helper — given the active workspace, returns:
 *   { plan, exhausted, tier, percent_used }
 * Tier is the visual tier (yellow|orange|red|null). Exhausted is true ONLY
 * for free plan with remaining<=0 — the composer reads this to render the
 * trial-exhausted block. */
export function getTrialState(workspace) {
  if (!workspace || workspace.plan !== "free") {
    return { plan: workspace?.plan, exhausted: false, tier: null, percent_used: 0 };
  }
  const initial = 300; // TRIAL_CENTS_DEFAULT — also exposed via /usage
  const remaining = Number(workspace.trial_anthropic_cents_remaining || 0);
  const used = Math.max(0, initial - remaining);
  const pct = initial ? (used / initial) * 100 : 0;
  return {
    plan: "free",
    exhausted: remaining <= 0,
    tier: pctToTier(pct),
    percent_used: pct,
    used_cents: used,
    remaining_cents: Math.max(0, remaining),
    initial_cents: initial,
  };
}

export default function TrialBanner({ workspace, onOpenSettings }) {
  const state = useMemo(() => getTrialState(workspace), [workspace]);
  const tier = state.tier;
  const wsId = workspace?.id;
  const lsKey = wsId && tier ? `onebrain:trial-banner:${wsId}:${tier}` : null;
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!lsKey) {
      setDismissed(false);
      return;
    }
    try {
      setDismissed(window.localStorage.getItem(lsKey) === "1");
    } catch {
      setDismissed(false);
    }
  }, [lsKey]);

  if (!tier) return null;
  if (dismissed) return null;

  const style = TIER_STYLES[tier];

  const dismiss = () => {
    if (lsKey) {
      try {
        window.localStorage.setItem(lsKey, "1");
      } catch {
        /* ignore */
      }
    }
    setDismissed(true);
  };

  const message = (() => {
    if (tier === "red") {
      return "Your free trial is exhausted. New messages to Claude are blocked until you upgrade.";
    }
    return `You've used ${Math.round(
      state.percent_used,
    )}% of your $3 free trial. (${fmtUsd(
      state.used_cents,
    )} of ${fmtUsd(state.initial_cents)}.)`;
  })();

  const ctaLabel = tier === "red" ? "Upgrade now" : "Manage in settings";

  return (
    <div
      className={`w-full px-4 sm:px-6 py-2.5 border-b ${style.bg} ${style.border}`}
      data-testid="trial-banner"
      data-tier={tier}
    >
      <div className="max-w-5xl mx-auto flex items-center gap-3">
        <AlertTriangle
          size={14}
          className={`${style.icon} shrink-0`}
          aria-hidden
        />
        <p
          className={`font-sans text-[13px] ${style.text} flex-1 leading-snug`}
        >
          {message}{" "}
          <button
            type="button"
            onClick={onOpenSettings}
            data-testid={
              tier === "red"
                ? "trial-banner-upgrade-now"
                : "trial-banner-open-settings"
            }
            className="underline font-medium hover:no-underline"
          >
            {ctaLabel}
          </button>
        </p>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          data-testid="trial-banner-dismiss"
          className={`p-1 rounded-md hover:bg-black/5 ${style.text} shrink-0`}
        >
          <X size={13} strokeWidth={2.2} />
        </button>
      </div>
    </div>
  );
}
